import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { HardDrive, MessageCircle, Tag, Users } from 'lucide-react';
import { API_TIMEOUT, apiBaseUrl } from '../config/api_config';
import { useLanguage } from '../i18n/LanguageContext';
import { computePlanFinalCost, formatDuration } from '../utils/planUtils';

export default function PricingPage() {
  const { t, lang } = useLanguage();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);

    axios
      .get(`${apiBaseUrl}/haseela/public/services-plans/`, { timeout: API_TIMEOUT })
      .then((res) => {
        if (!mounted) return;
        setPlans(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!mounted) return;
        setError(true);
        setPlans([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section className="section page-top">
      <div className="container">
        <div className="section-head">
          <h1>{t.pricing.title}</h1>
          <p>{t.pricing.subtitle}</p>
        </div>

        {loading && <p className="state-msg">{t.pricing.loading}</p>}
        {error && <p className="state-msg error">{t.pricing.error}</p>}
        {!loading && !error && plans.length === 0 && (
          <p className="state-msg">{t.pricing.empty}</p>
        )}

        <div className="pricing-grid">
          {plans.map((plan, index) => {
            const finalCost = computePlanFinalCost(plan);
            const hasDiscount =
              Number(plan.discount_perc) > 0 || Number(plan.discount_cost) > 0;
            const featured = index === Math.min(1, plans.length - 1);

            return (
              <article key={plan.id} className={`price-card ${featured ? 'featured' : ''}`}>
                <h2>{plan.name}</h2>
                <div className="price-amount">
                  <span className="amount">{finalCost}</span>
                  <span className="currency">{t.pricing.currency}</span>
                </div>
                {Number(plan.cost) !== Number(finalCost) && (
                  <p className="price-base">
                    {plan.cost} {t.pricing.currency}
                  </p>
                )}
                <p className="price-duration">
                  {t.pricing.duration}: {formatDuration(plan.duration_days, lang)}
                </p>

                <ul className="price-limits">
                  <li>
                    <Users size={16} />
                    {plan.max_customer_len} {t.pricing.customers}
                  </li>
                  <li>
                    <Users size={16} />
                    {plan.max_supplier_len} {t.pricing.suppliers}
                  </li>
                  <li>
                    <Users size={16} />
                    {plan.max_employee_len} {t.pricing.employees}
                  </li>
                </ul>

                <div className="price-extras">
                  {Number(plan.wa_cost) > 0 && (
                    <span>
                      <MessageCircle size={14} />
                      {t.pricing.whatsapp}: {plan.wa_cost} {t.pricing.currency}
                    </span>
                  )}
                  {Number(plan.backup_cost) > 0 && (
                    <span>
                      <HardDrive size={14} />
                      {t.pricing.backup}: {plan.backup_cost} {t.pricing.currency}
                    </span>
                  )}
                  {hasDiscount && (
                    <span>
                      <Tag size={14} />
                      {t.pricing.discount}
                      {plan.discount_name ? `: ${plan.discount_name}` : ''}
                      {Number(plan.discount_perc) > 0
                        ? ` ${plan.discount_perc}%`
                        : ` ${plan.discount_cost} ${t.pricing.currency}`}
                    </span>
                  )}
                </div>

                <Link
                  to="/demo"
                  state={{ planName: plan.name }}
                  className="btn btn-primary"
                >
                  {t.pricing.request}
                </Link>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
